import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchError, IssueDto, queryKeys, useAccessToken, UserDto } from "../../..";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  proposalGuid: string;
  issueGuid: string;
  user: UserDto;
};

const useUpvoteProposalRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editProposalStatusRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/proposals/${variables.proposalGuid}/upvotes`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editProposalStatusRequest };
};

const useUpvoteProposal = () => {
  const { editProposalStatusRequest } = useUpvoteProposalRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editProposalStatusRequest,
    onMutate: async (variables) => {
      let cachedIssueData: IssueDto | undefined;

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          proposals: cachedData.proposals.map((proposal) => {
            if (proposal.guid === variables.proposalGuid) {
              return {
                ...proposal,
                upvotes: [...proposal.upvotes, variables.user],
              };
            }

            return proposal;
          }),
        })
      );

      return { cachedIssueData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchIssue(variables.issueGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
    },
  });
};

export { useUpvoteProposal };
